import { inject } from '@angular/core';
import { Logger, RoomService } from '@core/services';
import { Observable } from 'rxjs';
import { Pagination, RoomEntity, RoomsListEntity } from '@core/entities';
import { FilterQuery } from '@core/types';
import { map } from 'rxjs/operators';
import { DateTimeUtility, GenerateImageUrlWithToken, removeNullAndUndefinedProperties } from '@core/utils';
import { SectionType } from '@core/enums';
import { plainToInstance } from 'class-transformer';
import { RRule } from 'rrule';

const log = new Logger('UseRoom');

export class UseRoom {
  private readonly _roomService = inject(RoomService);

  getAllRooms(pagination: Pagination, filterQuery: FilterQuery): Observable<RoomsListEntity> {
    filterQuery = removeNullAndUndefinedProperties({
      tags: filterQuery['tags'],
      instructor_type: filterQuery['instructorType'],
    });
    return this._roomService.find(pagination, filterQuery).pipe(
      map((res: RoomsListEntity) => {
        const transformedRooms = plainToInstance(RoomsListEntity, res);
        return {
          pagination: res.pagination,
          rooms: transformedRooms.rooms.map((room: RoomEntity) => {
            return this._transformRoomData(room);
          }),
        } as RoomsListEntity;
      }),
    );
  }

  getRoomById(id: number): Observable<RoomEntity> {
    return this._roomService.get(id).pipe(
      map((res: RoomEntity) => {
        const roomEntity = plainToInstance(RoomEntity, res);
        return this._transformRoomData(roomEntity);
      }),
    );
  }

  createRoom(formData: FormData): Observable<any> {
    return this._roomService.create(formData);
  }

  updateRoom(id: number, formData: FormData): Observable<any> {
    return this._roomService.update(id, formData);
  }

  deleteRoom(id: number): Observable<any> {
    return this._roomService.delete(id);
  }

  private _transformRoomData(room: RoomEntity): RoomEntity {
    const transformedRoom = room;
    if (room.image) {
      transformedRoom.imageUrl = GenerateImageUrlWithToken(room.id, SectionType.ROOM);
    }
    transformedRoom.availability = room.availability.map((availability) => {
      availability.timeFrom = availability.timeFrom.split(':').slice(0, 2).join(':');
      availability.timeTo = availability.timeTo.split(':').slice(0, 2).join(':');
      return availability;
    });
    transformedRoom.availability.sort((a, b) => {
      const weekdayA = isNaN(+a.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(a.weekday) : +a.weekday;
      const weekdayB = isNaN(+b.weekday) ? DateTimeUtility.convertWeekdayNameToNumber(b.weekday) : +b.weekday;
      return weekdayA - weekdayB;
    });

    if (transformedRoom?.roomBookings && transformedRoom?.roomBookings.length > 0) {
      transformedRoom.roomBookings = transformedRoom.roomBookings.map((booking) => {
        return {
          ...booking,
          startTime: booking.startTime.split(':').slice(0, 2).join(':'),
          endTime: booking.endTime.split(':').slice(0, 2).join(':'),
        };
      });

      transformedRoom.roomBookings.sort((a, b) => {
        const dateA = new Date(a.dateFrom);
        const dateB = new Date(b.dateFrom);
        return dateA.getTime() - dateB.getTime();
      });

      // Transform recurring room bookings
      transformedRoom.roomBookings = this._transformRecurringRoomBookings(transformedRoom.roomBookings);
    }

    if (transformedRoom?.roomBookingDetails && transformedRoom.roomBookingDetails.length > 0) {
      transformedRoom.roomBookingDetails = transformedRoom.roomBookingDetails.map((booking) => {
        booking.dates = booking.dates.map((date) => {
          return {
            ...date,
            startTime: date.startTime.split(':').slice(0, 2).join(':'),
            endTime: date.endTime.split(':').slice(0, 2).join(':'),
          };
        });

        return booking;
      });
    }

    return transformedRoom;
  }

  /**
   * Transforms room booking data by generating recurring booking dates based on course recurrence rules.
   * Similar to _generateAllEventDates in events composition but for room bookings.
   * @param roomBookings Array of room bookings to transform
   * @returns Array of room bookings with expanded recurring dates
   */
  private _transformRecurringRoomBookings(roomBookings: any[]): any[] {
    const expandedBookings: any[] = [];

    roomBookings.forEach((booking) => {
      // Check if the booking has a course with recurrence
      if (booking.course?.recurrence && !booking.course.recurrence.includes('null')) {
        try {
          const rruleSet = RRule.fromString(booking.course.recurrence);
          const recurringDates = rruleSet.all();

          recurringDates.forEach((recurringDate) => {
            const dateStr = recurringDate.toISOString().split('T')[0];

            // Create a new booking object for each recurring date
            const recurringBooking = {
              ...booking,
              dateFrom: dateStr,
              // Keep the original booking ID reference but this represents a recurring instance
              originalBookingId: booking.id,
              isRecurringInstance: true,
            };

            expandedBookings.push(recurringBooking);
          });
        } catch (error) {
          log.error('Error parsing RRule for room booking:', error);
          // If there's an error with RRule, keep the original booking
          expandedBookings.push(booking);
        }
      } else {
        // Non-recurring booking or no recurrence rule - keep as is
        expandedBookings.push(booking);
      }
    });

    return expandedBookings;
  }
}
